import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { useBulkSelection, BulkSelectBar, SelectPageCheckbox, SelectRowCheckbox } from '../../../shared/BulkSelect';
import { QuickBooksConnectionNotice } from '../../../shared/QuickBooksItemPicker';
import { QuickBooksCustomerPicker, QuickBooksIssuePills, QuickBooksDifferences, customerLabel } from '../../../shared/QuickBooksCustomerPicker';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import { formatNumber } from '../../../shared/numberFormat';
import { LocalPagination, useLocalPagination } from '../../../shared/pagination';
import UnderlinedTabs from './UnderlinedTabs';
import FieldHelp from '../../../shared/FieldHelp';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// QuickBooks > Customers (Link Customers to QuickBooks Customers,
// vis_quickbooks_integration.md "Customers" and "Bulk Matching for Existing
// Records"): the Customer field mapping, then linking each Customer to a
// QuickBooks Customer — an existing one, or a new one created from Optim's
// details. Unmatched lists active Customers with no link, each with any
// suggestion (the one QuickBooks Customer matching by name or billing
// email); Matched and Needs Attention list the links. Optim is the master
// of the linked details, so linking overwrites QuickBooks — the differences
// are shown before it does. Unlinked Customers is the other way round: the
// QuickBooks Customers no Optim Customer is linked to.

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '8px', fontSize: 14, verticalAlign: 'top' };
const SMALL_PRIMARY = { padding: '5px 12px', border: 'none', borderRadius: 6, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };
const FIELD_LABEL = { fontSize: 12, color: '#5a6b85' };

const REASON_PILLS = {
  both: { label: 'Name & email match', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' },
  email: { label: 'Email match', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' },
  name: { label: 'Name match', color: '#92400e', background: 'rgba(217, 119, 6, 0.14)' },
};

const MAPPING_FIELDS = [
  { key: 'billing_address', label: 'Billing Address', from: 'address' },
  { key: 'shipping_address', label: 'Shipping Address', from: 'address' },
  { key: 'billing_email', label: 'Billing Email', from: 'email', optional: true },
];

function matchesSearch(row, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  const fields = [row.customer.name, row.link?.qbo_customer_name, row.suggestion?.customer.name, row.suggestion?.customer.email];
  return fields.some((f) => f && String(f).toLowerCase().includes(t));
}

// The Customer field mapping: which Address Type fills QuickBooks' Billing
// and Shipping Address, and which Email Type its Billing Email (optional:
// Don't sync leaves QuickBooks' email as it is).
function FieldMappingCard({ mapping, canEdit, onSaved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [types, setTypes] = useState({ address: [], email: [] });
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([axios.get('/api/address-types'), axios.get('/api/email-types')])
      .then(([a, e]) => setTypes({ address: a.data.data || [], email: e.data.data || [] }))
      .catch((err) => showError(err, 'Failed to load Address and Email Types'));
    // eslint-disable-next-line
  }, []);

  const editing = draft !== null;
  // Until the first load lands there's no mapping yet; don't flash it as unmapped.
  const loaded = mapping !== undefined;
  const unmapped = loaded && !mapping?.complete;
  const startEdit = () => setDraft(Object.fromEntries(MAPPING_FIELDS.map((f) => [f.key, mapping?.[f.key]?.id || ''])));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/api/quickbooks/customer-field-mapping', draft);
      setDraft(null);
      dismissError();
      onSaved();
    } catch (err) {
      showError(err, 'Saving the field mapping failed');
    }
    setSaving(false);
  };

  return (
    <div style={{ margin: '16px 24px 0', background: '#fff', border: `1px solid ${unmapped ? '#f0d58c' : '#b3bbc7'}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>Customer field mapping</strong>
        <FieldHelp fieldKey="accounting-quickbooks.customer-field-mapping" pageKey="accounting-quickbooks" label="QuickBooks — Customer field mapping">
          Which of a Customer’s Address Types fills the QuickBooks Billing and Shipping Address, and which Email Type fills the Billing Email. Where a Customer has several of that type, the primary one is used, otherwise the first; where it has none, the QuickBooks field is left blank. Choose Don’t sync for the Billing Email to leave QuickBooks’ email as it is. Changing the mapping updates every linked QuickBooks Customer.
        </FieldHelp>
        {!editing && canEdit && loaded && (
          <button type="button" onClick={startEdit} title="Edits which Optim types fill the QuickBooks Customer fields" style={{ ...LINK_BUTTON, marginLeft: 'auto' }}>
            {mapping?.complete ? 'Change' : 'Map fields'}
          </button>
        )}
      </div>
      {unmapped && !editing && (
        <div style={{ fontSize: 13, color: '#92400e', marginTop: 6 }}>
          Customers can’t be linked or created in QuickBooks until these fields are mapped.{!canEdit && ' Ask someone who can edit the QuickBooks screen to map them.'}
        </div>
      )}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 10 }}>
        {MAPPING_FIELDS.map((field) => (
          <div key={field.key} style={{ minWidth: 200 }}>
            <label style={FIELD_LABEL}>QuickBooks {field.label} ← {field.from === 'address' ? 'Address Type' : 'Email Type'}</label>
            {editing ? (
              <select className="styled-input" value={draft[field.key]} onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })} style={{ display: 'block', marginTop: 4, padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' }}>
                <option value="">{field.optional ? 'Don’t sync' : 'Choose...'}</option>
                {types[field.from].map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: 14, marginTop: 4 }}>{!loaded ? '…' : mapping?.[field.key]?.name || (field.optional ? 'Don’t sync' : <span style={{ color: '#b91c1c' }}>Not mapped</span>)}</div>
            )}
          </div>
        ))}
      </div>
      {editing && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <SaveButton type="button" onClick={save} loading={saving} disabled={MAPPING_FIELDS.some((f) => !f.optional && !draft[f.key])} />
          <CancelButton onClick={() => setDraft(null)} disabled={saving} />
        </div>
      )}
    </div>
  );
}

function QuickBooksCustomersTab({ canEdit, canEditMapping }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState('unmatched');
  const [search, setSearch] = useState('');
  // The row being linked: { entityId, mode: 'pick' | 'confirm', customer, differences }.
  const [working, setWorking] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  // ?customer= (from a Sales Order's "Not linked to QuickBooks"): open on
  // that Customer's row, its suggestion ready to confirm, else the picker.
  const [searchParams] = useSearchParams();
  const focusCustomerId = searchParams.get('customer');
  const focusedRef = useRef(false);

  const load = async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/quickbooks/customers', { params: refresh ? { refresh: 1 } : {} });
      setData(res.data.data);
    } catch (err) {
      showError(err, 'Failed to load QuickBooks Customer links');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  // Coming back from a Customer opened in another tab shows its changes.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line
  }, []);

  const connected = data?.connection?.status === 'connected';
  const mapped = !!data?.mapping?.complete;
  const editable = canEdit && connected && mapped;
  // Must match the header, or the loading/empty row adds phantom columns and the header stops short
  const columnCount = list === 'unmatched' ? (editable ? 4 : 2) : (canEdit && connected ? 4 : 3);

  const rows = useMemo(() => (list === 'qbo_customers' ? [] : (data?.[list] || []).filter((row) => matchesSearch(row, search))), [data, list, search]);
  // Unlinked QuickBooks Customers, each with the Optim Customer it's
  // suggested for, if any.
  const qboRows = useMemo(() => {
    if (list !== 'qbo_customers' || !data) return [];
    const suggestedFor = new Map(data.unmatched.filter((r) => r.suggestion).map((r) => [r.suggestion.customer.id, r.customer]));
    const t = search.toLowerCase();
    return data.customers
      .filter((c) => !t || [c.name, c.company_name, c.email].some((f) => f && f.toLowerCase().includes(t)))
      .map((customer) => ({ customer, suggestedFor: suggestedFor.get(customer.id) || null }));
  }, [data, list, search]);
  const paging = useLocalPagination(list === 'qbo_customers' ? qboRows : rows, `${list}|${search}`);
  const acceptableIn = (some) => (list === 'unmatched' && editable ? some.filter((r) => r.suggestion).map((r) => r.customer.id) : []);
  const selection = useBulkSelection({ pageIds: acceptableIn(paging.pageRows), total: acceptableIn(rows).length, resetKey: `${list}|${search}|${data?.fetched_at}` });

  const close = () => setWorking(null);

  useEffect(() => {
    if (!data || !focusCustomerId || focusedRef.current) return;
    focusedRef.current = true;
    const listKey = ['unmatched', 'needs_attention', 'matched'].find((key) => (data[key] || []).some((r) => String(r.customer.id) === focusCustomerId));
    if (!listKey) return;
    const row = data[listKey].find((r) => String(r.customer.id) === focusCustomerId);
    setList(listKey);
    setSearch(row.customer.name);
    if (!editable) return;
    if (row.suggestion) confirmLink(row.customer.id, row.suggestion.customer, row.suggestion.differences);
    else setWorking({ entityId: row.customer.id, mode: 'pick' });
    // eslint-disable-next-line
  }, [data, focusCustomerId, editable]);

  // A reload (e.g. coming back from editing the Customer in another tab)
  // re-compares the open confirm, so it shows what linking will send now.
  useEffect(() => {
    if (!data || working?.mode !== 'confirm') return;
    const { entityId, customer } = working;
    axios.get(`/api/quickbooks/customers/${entityId}/compare`, { params: { qbo_customer_id: customer.id } })
      .then((res) => setWorking((prev) => (prev?.mode === 'confirm' && prev.entityId === entityId && prev.customer.id === customer.id
        ? { ...prev, differences: res.data.data.differences } : prev)))
      .catch((err) => showError(err, 'Couldn’t compare with QuickBooks'));
    // eslint-disable-next-line
  }, [data]);
  const toggleExpanded = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const run = async (fn, failMessage) => {
    setBusy(true);
    try {
      await fn();
      dismissError();
      await load();
    } catch (err) {
      showError(err, failMessage);
    }
    setBusy(false);
  };

  // Shows how QuickBooks differs before linking overwrites it.
  const confirmLink = async (entityId, customer, differences) => {
    if (differences) {
      setWorking({ entityId, mode: 'confirm', customer, differences });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.get(`/api/quickbooks/customers/${entityId}/compare`, { params: { qbo_customer_id: customer.id } });
      setWorking({ entityId, mode: 'confirm', customer, differences: res.data.data.differences });
    } catch (err) {
      showError(err, 'Couldn’t compare with QuickBooks');
    }
    setBusy(false);
  };

  const link = (entityId, customer) => run(async () => {
    await axios.put(`/api/quickbooks/customers/${entityId}`, { qbo_customer_id: customer.id });
    close();
  }, 'Linking failed');

  const create = async (row) => {
    if (!(await askConfirm(`Create ${row.customer.name} in QuickBooks from Optim’s details, and link it?`, { confirmLabel: 'Create and Link' }))) return;
    run(async () => {
      await axios.post(`/api/quickbooks/customers/${row.customer.id}/create`);
      close();
    }, 'Creating in QuickBooks failed');
  };

  const retry = (row) => run(() => axios.post(`/api/quickbooks/customers/${row.customer.id}/retry`), 'Sending to QuickBooks failed');

  const unlink = async (row) => {
    if (!(await askConfirm(`Remove the QuickBooks link for ${row.customer.name}?\n\nIt’s linked to “${row.link.qbo_customer_name}”. Nothing changes in QuickBooks, and Optim’s changes stop going to it.`, { confirmLabel: 'Remove', destructive: true }))) return;
    run(() => axios.delete(`/api/quickbooks/customers/${row.customer.id}`), 'Removing the link failed');
  };

  const acceptSelected = async () => {
    const chosen = rows.filter((r) => r.suggestion && selection.isSelected(r.customer.id));
    if (!chosen.length) return;
    const differing = chosen.filter((r) => r.suggestion.differences.length).length;
    const overwrite = differing ? `\n\nQuickBooks details will be overwritten with Optim’s for ${differing} Customer${differing === 1 ? '' : 's'}.` : '';
    if (!(await askConfirm(`Link ${chosen.length} Customer${chosen.length === 1 ? '' : 's'} to their suggested QuickBooks Customers?${overwrite}`, { confirmLabel: 'Link' }))) return;
    await run(async () => {
      await axios.post('/api/quickbooks/customers/bulk-link', {
        links: chosen.map((r) => ({ commercial_entity_id: r.customer.id, qbo_customer_id: r.suggestion.customer.id })),
      });
      selection.clear();
    }, 'Accepting suggestions failed');
  };

  // Opens in a new tab, so details can be keyed in on the Customer and the
  // matching list is still here to come back to.
  const openCustomer = (id) => window.open(`/commercial-entities/${id}`, '_blank', 'noopener');

  const listHelp = (key, label, text) => (
    <FieldHelp fieldKey={`accounting-quickbooks.customers-${key}`} pageKey="accounting-quickbooks" label={`QuickBooks Customers — ${label}`}>{text}</FieldHelp>
  );
  const lists = [
    {
      key: 'unmatched',
      label: `Unmatched (${formatNumber(data?.unmatched.length || 0)})`,
      help: listHelp('unmatched', 'Unmatched', 'Active Customers not yet linked to a QuickBooks Customer. Where exactly one QuickBooks Customer has the same name or billing email it’s suggested; accept it, choose another, or create a new one in QuickBooks from Optim’s details.'),
    },
    {
      key: 'matched',
      label: `Matched (${formatNumber(data?.matched.length || 0)})`,
      help: listHelp('matched', 'Matched', 'Customers linked to a QuickBooks Customer and in step with it. Changes in Optim to the name, mapped addresses, billing email or Payment Term go to QuickBooks automatically.'),
    },
    {
      key: 'needs_attention',
      label: `Needs Attention (${formatNumber(data?.needs_attention.length || 0)})`,
      help: listHelp('needs-attention', 'Needs Attention', 'Links where the QuickBooks Customer is inactive or gone, the last change couldn’t be sent (retry it once fixed), the Payment Term isn’t matched to a QuickBooks Term yet, or the record no longer holds a Customer relationship.'),
    },
    {
      key: 'qbo_customers',
      label: `Unlinked Customers (${formatNumber(data?.customers.length || 0)})`,
      help: listHelp('unlinked', 'Unlinked Customers', 'The other way round: active, top-level QuickBooks Customers no Optim Customer is linked to. Some are expected to stay QuickBooks-only, so this list is for reference. Links are always made from the Optim Customer.'),
    },
  ];

  // unlinkedRow: the Unmatched row being picked for, which can also be
  // created in QuickBooks when it isn't there to pick.
  const renderPicker = (entityId, unlinkedRow) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <QuickBooksCustomerPicker customers={data.customers} value={null} onChange={(c) => c && confirmLink(entityId, c)} autoFocus disabled={busy} />
      </div>
      {unlinkedRow && (
        <button type="button" onClick={() => create(unlinkedRow)} disabled={busy} title="Creates this Customer in QuickBooks and links it" style={{ ...SMALL_PRIMARY, flexShrink: 0, whiteSpace: 'nowrap', cursor: busy ? 'not-allowed' : 'pointer' }}>
          Create in QuickBooks
        </button>
      )}
      <CancelButton onClick={close} disabled={busy} />
    </div>
  );

  const renderConfirm = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 14 }}>{customerLabel(working.customer)}</div>
      <QuickBooksDifferences differences={working.differences} />
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => link(working.entityId, working.customer)} disabled={busy} title="Links this Customer and sends Optim’s details to QuickBooks" style={{ ...SMALL_PRIMARY, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: busy ? 'not-allowed' : 'pointer' }}>
          {busy && <Loader2 size={14} style={{ animation: 'qb-spin 0.8s linear infinite' }} />}
          {busy ? 'Linking...' : working.differences.length ? 'Link and overwrite QuickBooks' : 'Link'}
        </button>
        <CancelButton onClick={close} disabled={busy} />
      </div>
    </div>
  );

  const nameCell = (row) => (
    <td className="cell-title" style={TD}>
      <button type="button" onClick={() => openCustomer(row.customer.id)} title="Opens this Customer in a new tab" style={{ ...LINK_BUTTON, fontSize: 14, textDecoration: 'none', color: '#222', textAlign: 'left' }}>
        {row.customer.name}
      </button>
      {!row.customer.is_active && <span style={{ marginLeft: 6, fontSize: 12, color: '#888' }}>(inactive)</span>}
    </td>
  );

  const unmatchedRow = (row) => {
    const id = row.customer.id;
    const isWorking = working?.entityId === id;
    const s = row.suggestion;
    return (
      <tr key={id} className="list-row">
        {editable && (
          <td className="cell-select" style={{ ...TD, textAlign: 'center' }}>
            {s && !isWorking && <SelectRowCheckbox selection={selection} id={id} />}
          </td>
        )}
        {nameCell(row)}
        <td data-label="Suggested QuickBooks Customer" style={TD}>
          {isWorking && working.mode === 'pick' ? renderPicker(id, row) : isWorking ? renderConfirm() : s ? (
            <div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {customerLabel(s.customer)}
                <Pill {...REASON_PILLS[s.reason]}>{REASON_PILLS[s.reason].label}</Pill>
                {s.differences.length > 0 && (
                  <button type="button" onClick={() => toggleExpanded(id)} title="Shows how QuickBooks differs from Optim" style={{ ...LINK_BUTTON, display: 'inline-flex', alignItems: 'center', gap: 2, color: '#92400e' }}>
                    {expanded.has(id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Details differ
                  </button>
                )}
              </span>
              {expanded.has(id) && <div style={{ marginTop: 8 }}><QuickBooksDifferences differences={s.differences} /></div>}
            </div>
          ) : (
            <span style={{ color: '#888', fontSize: 13 }}>{connected ? 'No suggestion' : ''}</span>
          )}
        </td>
        {editable && (
          <td style={{ ...TD, whiteSpace: 'nowrap' }}>
            {!isWorking && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                {s && (
                  <button type="button" onClick={() => confirmLink(id, s.customer, s.differences)} disabled={busy} title="Links this Customer to the suggested QuickBooks Customer" style={{ ...SMALL_PRIMARY, cursor: busy ? 'not-allowed' : 'pointer' }}>
                    Accept
                  </button>
                )}
                <RowActionsMenu
                  items={[
                    { key: 'choose', label: s ? 'Choose another' : 'Choose QuickBooks Customer', disabled: busy, onClick: () => setWorking({ entityId: id, mode: 'pick' }) },
                    { key: 'create', label: 'Create in QuickBooks', disabled: busy, onClick: () => create(row) },
                  ]}
                />
              </span>
            )}
          </td>
        )}
      </tr>
    );
  };

  const linkedRow = (row) => {
    const id = row.customer.id;
    const isWorking = working?.entityId === id;
    const failed = row.link.issues.some((i) => i.code === 'push_failed');
    return (
      <tr key={id} className="list-row">
        {nameCell(row)}
        <td data-label="QuickBooks Customer" style={TD}>
          {isWorking && working.mode === 'pick' ? renderPicker(id) : isWorking ? renderConfirm() : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span>{row.link.qbo_customer_name}</span>
              <QuickBooksIssuePills issues={row.link.issues} />
              {failed && <span style={{ fontSize: 12, color: '#b91c1c' }}>{row.link.push_error}</span>}
            </div>
          )}
        </td>
        <td data-label="Last sent" style={{ ...TD, fontSize: 13, color: '#666' }}>{row.link.pushed_at ? formatDateTime(row.link.pushed_at) : '—'}</td>
        {canEdit && connected && (
          <td className="cell-actions" style={{ ...TD, textAlign: 'center' }}>
            {!isWorking && (
              <RowActionsMenu
                items={[
                  ...(failed ? [{ key: 'retry', label: 'Retry sending to QuickBooks', disabled: busy, onClick: () => retry(row) }] : []),
                  ...(mapped ? [{ key: 'change', label: 'Change QuickBooks Customer', disabled: busy, onClick: () => setWorking({ entityId: id, mode: 'pick' }) }] : []),
                  { key: 'remove', label: 'Remove link', danger: true, disabled: busy, onClick: () => unlink(row) },
                ]}
              />
            )}
          </td>
        )}
      </tr>
    );
  };

  const emptyText = { unmatched: 'Every active Customer is linked', matched: 'No Customers are linked and in step yet', needs_attention: 'Nothing needs attention' }[list];

  return (
    <>
      <FieldMappingCard mapping={data?.mapping} canEdit={canEditMapping} onSaved={load} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '16px 24px 0' }}>
        <input className="styled-input" placeholder="Search name or QuickBooks Customer..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
        <button
          type="button"
          onClick={() => load({ refresh: true })}
          disabled={loading}
          title="Fetches the Customers from QuickBooks again"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', fontSize: 14, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? <Loader2 size={15} style={{ animation: 'qb-spin 0.8s linear infinite' }} /> : <RefreshCw size={15} />}
          Refresh from QuickBooks
        </button>
        {data?.fetched_at && (
          <span style={{ fontSize: 13, color: '#666' }}>
            {formatNumber(data.customers.length)} unlinked QuickBooks Customer{data.customers.length === 1 ? '' : 's'} · Customers fetched {formatDateTime(data.fetched_at)}
          </span>
        )}
        <style>{'@keyframes qb-spin { to { transform: rotate(360deg); } }'}</style>
      </div>

      <QuickBooksConnectionNotice connection={data?.connection} style={{ margin: '12px 24px 0' }} />

      <div style={{ margin: '16px 24px 0' }}>
        <UnderlinedTabs tabs={lists} value={list} onChange={(key) => { setList(key); close(); }} />
      </div>

      {list === 'unmatched' && (
        <BulkSelectBar selection={selection} noun="suggestions" actions={[{ key: 'accept', label: busy ? 'Linking...' : 'Accept suggestions', onClick: acceptSelected }]} />
      )}

      {list === 'qbo_customers' ? (
        <div style={{ margin: '16px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
            <colgroup>
              <col />
              <col style={{ width: '28%' }} />
              <col style={{ width: '28%' }} />
            </colgroup>
            <thead className="list-head" style={{ fontSize: 14 }}>
              <tr>
                <th style={TH}>QuickBooks Customer</th>
                <th style={TH}>Email</th>
                <th style={TH}>Suggested for</th>
              </tr>
            </thead>
            <tbody>
              {!data && loading ? (
                <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Fetching Customers from QuickBooks...</td></tr>
              ) : qboRows.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>
                    {search ? 'No results' : connected ? 'Every active QuickBooks Customer is linked' : 'Customers can’t be fetched from QuickBooks right now'}
                  </td>
                </tr>
              ) : paging.pageRows.map(({ customer, suggestedFor }) => (
                <tr key={customer.id} className="list-row">
                  <td className="cell-title" style={TD}>
                    {customer.name}
                    {customer.company_name && customer.company_name !== customer.name && <div style={{ fontSize: 12, color: '#666' }}>{customer.company_name}</div>}
                  </td>
                  <td data-label="Email" style={TD}>{customer.email}</td>
                  <td data-label="Suggested for" style={TD}>
                    {suggestedFor ? (
                      <button type="button" onClick={() => openCustomer(suggestedFor.id)} title="Opens this Customer in a new tab" style={{ ...LINK_BUTTON, fontSize: 14, textDecoration: 'none', color: '#222', textAlign: 'left' }}>
                        {suggestedFor.name}
                      </button>
                    ) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div style={{ margin: '16px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          {list === 'unmatched' ? (
            <>
              <colgroup>
                {editable && <col style={{ width: '4%' }} />}
                <col style={{ width: '30%' }} />
                <col />
                {editable && <col style={{ width: '14%' }} />}
              </colgroup>
              <thead className="list-head" style={{ fontSize: 14 }}>
                <tr>
                  {editable && <th style={{ ...TH, textAlign: 'center' }}>{selection.pageIds.length > 0 && <SelectPageCheckbox selection={selection} />}</th>}
                  <th style={TH}>Customer</th>
                  <th style={TH}>Suggested QuickBooks Customer</th>
                  {editable && <th style={TH}></th>}
                </tr>
              </thead>
            </>
          ) : (
            <>
              <colgroup>
                <col style={{ width: '30%' }} />
                <col />
                <col style={{ width: '18%' }} />
                {canEdit && connected && <col style={{ width: '6%' }} />}
              </colgroup>
              <thead className="list-head" style={{ fontSize: 14 }}>
                <tr>
                  <th style={TH}>Customer</th>
                  <th style={TH}>QuickBooks Customer</th>
                  <th style={TH}>Last sent</th>
                  {canEdit && connected && <th style={TH}></th>}
                </tr>
              </thead>
            </>
          )}
          <tbody>
            {!data && loading ? (
              <tr><td colSpan={columnCount} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Fetching Customers from QuickBooks...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={columnCount} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>{search ? 'No results' : emptyText}</td></tr>
            ) : list === 'unmatched' ? paging.pageRows.map(unmatchedRow) : paging.pageRows.map(linkedRow)}
          </tbody>
        </table>
      </div>
      )}
      {paging.pagination.total > 0 && <LocalPagination paging={paging} totalLabel="Customers" />}
    </>
  );
}

export default QuickBooksCustomersTab;
